import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Home, Trophy, Users, Grid3x3, Settings, LogOut, Menu, X, Info, BookOpen, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useState } from 'react';
import TournamentSelector from './TournamentSelector';
import { useTournament } from '../context/TournamentContext';
import { clearToken } from '@/lib/auth';

const API = '/api';

export default function Navigation({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { tournaments, selectedTournamentId, selectTournament } = useTournament();

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      clearToken();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      clearToken();
      toast.error('Logout failed');
      navigate('/login');
    }
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/picks', label: 'My Picks', icon: Grid3x3 },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
    // { path: '/bracket', label: 'Bracket', icon: Grid3x3 },  // Hidden - replaced by My Bracket
    { path: '/my-bracket', label: 'My Bracket', icon: Trophy },
    { path: '/talk-smack', label: 'Talk Smack', icon: MessageCircle },
    { path: '/about', label: 'About', icon: Info },
  ];

  if (user?.is_admin) {
    navItems.push({ path: '/admin', label: 'Admin', icon: Settings });
    navItems.push({ path: '/admin-guide', label: 'Admin Guide', icon: BookOpen });
  }

  return (
    <nav className="bg-emerald-900 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-8">
            <Link to="/dashboard" className="flex items-center space-x-2">
              <span className="text-2xl">☘️</span>
              <span className="font-bebas text-xl tracking-wider text-amber-600">The Shamrock</span>
            </Link>
            <div className="hidden md:flex space-x-4">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-manrope transition-colors ${
                      isActive
                        ? 'bg-amber-600 text-white'
                        : 'text-emerald-100 hover:bg-emerald-800'
                    }`}
                  >
                    <Icon size={16} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <TournamentSelector
              tournaments={tournaments}
              selectedTournamentId={selectedTournamentId}
              onSelect={selectTournament}
            />
            <div className="hidden sm:flex items-center space-x-2">
              {user?.picture && (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full border-2 border-amber-600"
                />
              )}
              <span className="font-manrope text-sm">{user?.name}</span>
            </div>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="hidden sm:flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-manrope bg-red-600 hover:bg-red-700 transition-colors"
            >
              <LogOut size={16} />
              <span>Logout</span>
            </button>
            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-white hover:bg-emerald-800"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-emerald-800 border-t border-emerald-700">
          <div className="px-4 py-3 space-y-2">
            {/* Tournament Selector in Mobile Menu */}
            <div className="px-4 py-2">
              <TournamentSelector
                tournaments={tournaments}
                selectedTournamentId={selectedTournamentId}
                onSelect={selectTournament}
              />
            </div>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-md font-manrope transition-colors ${
                    isActive
                      ? 'bg-amber-600 text-white'
                      : 'text-emerald-100 hover:bg-emerald-700'
                  }`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            <div className="pt-3 border-t border-emerald-700 mt-3">
              <div className="flex items-center space-x-3 px-4 py-2 text-emerald-100">
                {user?.picture && (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full border-2 border-amber-600"
                  />
                )}
                <span className="font-manrope text-sm">{user?.name}</span>
              </div>
              <button
                data-testid="mobile-logout-button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors mt-2"
              >
                <LogOut size={20} />
                <span className="font-manrope">Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}